import { ReactNode, useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import { useNavigate } from "react-router-dom";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, DataKitItemDto } from "../../../../domain/DataKitDto";
import { env } from "../../../../env/env";
import { dataKitThumbColors } from "./DataKitThumb";
import "./DataKitItemSearch.scss";

interface Result {
  item: DataKitItemDto;
  kit: DataKitDto;
}

interface Props {
  query: string;
  /** The account's active kits - archived kits are out of the search along with archived items. */
  kits: DataKitDto[];
}

/** Wraps each case-insensitive occurrence of `q` in a <mark>. */
function highlight(text: string, q: string): ReactNode {
  if (!text || !q) return text;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const out: ReactNode[] = [];
  let from = 0;
  let at = lower.indexOf(needle);
  while (at >= 0) {
    if (at > from) out.push(text.slice(from, at));
    out.push(<mark key={at}>{text.slice(at, at + needle.length)}</mark>);
    from = at + needle.length;
    at = lower.indexOf(needle, from);
  }
  if (from < text.length) out.push(text.slice(from));
  return out;
}

/**
 * Search item names and descriptions in every kit at once (Oct 6: in v1, replace is not) - so a
 * supplier or ingredient rename can be traced to every item that mentions it without opening each
 * kit. Kit data only: text typed straight onto designs isn't in here.
 *
 * Built on the per-kit item lists the kit page already loads rather than a new endpoint: each kit's
 * active items are fetched once, the first time a search runs, and filtered here as you type.
 * `usedOnCount` is the same "used on N menus" the kit's Items tab shows.
 */
export function DataKitItemSearch({ query, kits }: Props) {
  const navigate = useNavigate();
  const [itemsByKit, setItemsByKit] = useState<
    Record<string, DataKitItemDto[]>
  >({});
  const [loading, setLoading] = useState(false);

  const kitIds = useMemo(
    () => kits.map((k) => k.id).filter((id): id is string => !!id),
    [kits]
  );

  useEffect(() => {
    const missing = kitIds.filter((id) => !(id in itemsByKit));
    if (missing.length === 0) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(
      missing.map((id) =>
        DataKitApi.listItems(id, false)
          .then((res) => [id, res.data || []] as const)
          // One kit failing to load shouldn't blank the others' results.
          .catch(() => [id, [] as DataKitItemDto[]] as const)
      )
    ).then((loaded) => {
      if (cancelled) return;
      setItemsByKit((prev) => {
        const next = { ...prev };
        loaded.forEach(([id, items]) => {
          next[id] = items;
        });
        return next;
      });
      // In the same tick as the items, not in a .finally - storing them re-runs this effect, and
      // its cleanup would otherwise mark this run cancelled before loading was ever cleared.
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [kitIds, itemsByKit]);

  const q = query.trim();

  const results: Result[] = useMemo(() => {
    const needle = q.toLowerCase();
    if (!needle) return [];
    const out: Result[] = [];
    kits.forEach((kit) => {
      (itemsByKit[kit.id || ""] || []).forEach((item) => {
        if (item.archived) return;
        if (
          (item.name || "").toLowerCase().includes(needle) ||
          (item.description || "").toLowerCase().includes(needle)
        ) {
          out.push({ item, kit });
        }
      });
    });
    return out.sort((a, b) =>
      (a.item.name || "").localeCompare(b.item.name || "", undefined, {
        sensitivity: "base",
      })
    );
  }, [q, kits, itemsByKit]);

  const kitCount = useMemo(
    () => new Set(results.map((r) => r.kit.id)).size,
    [results]
  );

  const columns = [
    {
      title: "Item",
      key: "item",
      render: (_: unknown, r: Result) => (
        <div className="item-search-name-cell">
          <div className="item-search-name">
            {highlight(r.item.name || "Untitled", q)}
          </div>
          {r.item.description && (
            <div className="item-search-desc">
              {highlight(r.item.description, q)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Library",
      key: "kit",
      width: 240,
      render: (_: unknown, r: Result) => {
        const { bg, fg } = dataKitThumbColors(r.kit.name || "Untitled");
        return (
          <span
            className="item-search-kit-chip"
            style={{ background: bg, color: fg }}
          >
            {r.kit.name || "Untitled"}
          </span>
        );
      },
    },
    {
      title: "Menus",
      key: "menus",
      width: 140,
      align: "right" as const,
      render: (_: unknown, r: Result) =>
        r.item.usedOnCount > 0 ? (
          `Used on ${r.item.usedOnCount}`
        ) : (
          <span className="item-search-unused">Not on a menu</span>
        ),
    },
  ];

  const settled = !loading || results.length > 0;

  return (
    <div className="data-kit-item-search">
      {settled && results.length > 0 && (
        <p className="item-search-summary">
          {results.length} item{results.length === 1 ? "" : "s"} in {kitCount}{" "}
          {kitCount === 1 ? "library" : "libraries"}
        </p>
      )}

      <Table
        className="data-kit-table item-search-table"
        dataSource={results}
        columns={columns}
        rowKey={(r) => r.item.id || `${r.kit.id}:${r.item.name}`}
        loading={loading && results.length === 0}
        pagination={false}
        rowClassName="data-kit-row"
        locale={{
          emptyText: loading ? (
            " "
          ) : (
            <div className="item-search-empty">
              <h3>No items match &ldquo;{q}&rdquo;</h3>
              <p>
                Search looks at item names and descriptions in every library.
                Text typed directly on designs isn&apos;t included.
              </p>
            </div>
          ),
        }}
        onRow={(r) => ({
          onClick: () => {
            if (!r.kit.id || !r.item.id) return;
            navigate(
              `${env.dashboardPath}/account/data-kits/${
                r.kit.id
              }?item=${encodeURIComponent(r.item.id)}`
            );
          },
        })}
      />

      {results.length > 0 && (
        <p className="item-search-footnote">
          Archived items and text typed directly on designs aren&apos;t
          included.
        </p>
      )}
    </div>
  );
}
